import { useEffect, useRef } from 'react';
import useAnimationControls from './useAnimationControls';

// Pauses together with animations to stay in sync with animation driven paging
export default function usePausableInterval(
  callback: (() => void) | undefined,
  intervalInMilliseconds: number | undefined,
): void {
  const { isPaused } = useAnimationControls();
  const callbackRef = useRef(callback);
  const remainingTimeRef = useRef<number | undefined>(undefined);
  const isActive = callback !== undefined;

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    remainingTimeRef.current = undefined;
  }, [isActive, intervalInMilliseconds]);

  useEffect(() => {
    if (!isActive || isPaused || intervalInMilliseconds === undefined) {
      return;
    }

    const interval = intervalInMilliseconds;
    let timerId: number;
    let delay: number;
    let startTime: number;

    function schedule(newDelay: number) {
      delay = newDelay;
      startTime = Date.now();
      timerId = window.setTimeout(() => {
        callbackRef.current?.();
        schedule(interval);
      }, delay);
    }

    schedule(remainingTimeRef.current ?? interval);

    return () => {
      window.clearTimeout(timerId);
      remainingTimeRef.current = Math.max(0, delay - (Date.now() - startTime));
    };
  }, [isActive, isPaused, intervalInMilliseconds]);
}
